/**
 * Les appels d'une conversation de checkpoint : les mêmes points d'API que l'ancien écran
 * (`liveService` : créer, répondre, finir, donner son avis, gagner le pétale), appelés en direct et
 * non par la tranche Redux — le moteur gère lui-même ses échecs, et aucune boîte « Pas de connexion »
 * ne s'ouvre à sa place. La tranche `createConversation` reste à celui du live (la main levée), que
 * le starting block ne touche plus.
 *
 * La photo d'une réponse part par le stockage présigné, comme avant, mais l'envoi est VÉRIFIÉ :
 * l'ancien `vaporStore` ne lisait pas la réponse du `PUT`, et une photo refusée par le stockage
 * passait pour envoyée. Son nom ne porte plus le prénom de l'élève.
 */
import axios from "axios"

import { API_URL } from "../../../data/constants/apiConstants"
import { liveService } from "../../../data/services/liveService"
import { fetchResourceFromURI } from "../../../data/utility/fileFromURI"
import { type ApiCheckpointConversation } from "./checkpointConversationMachine"

type Id = number | string

export type CheckpointApi = {
    create: (body: {
        student_id: Id
        live_session_id: Id
        trigger_context: string
        activity?: Id
    }) => Promise<ApiCheckpointConversation>
    respond: (
        conversationId: Id,
        body: { student_response: string; activity?: Id; checkpoint_attachments?: string[] }
    ) => Promise<ApiCheckpointConversation>
    finish: (conversationId: Id, body: { interruption_reason: string; ended_at: string }) => Promise<unknown>
    feedback: (conversationId: Id, value: 1 | -1) => Promise<unknown>
    awardPetal: (conversationId: Id) => Promise<unknown>
    /** Envoie la photo au stockage et rend sa clé. */
    uploadPhoto: (uri: string) => Promise<string>
}

/** Le stockage a refusé la photo : son code, rien d'autre. */
export class PhotoUploadError extends Error {
    readonly status: number
    constructor(status: number) {
        super("le stockage a refusé la photo")
        this.name = "PhotoUploadError"
        this.status = status
    }
}

type Envelope = { data?: { data?: ApiCheckpointConversation } }
const payload = (response: Envelope): ApiCheckpointConversation => response?.data?.data ?? {}

async function uploadPhoto(uri: string): Promise<string> {
    const file = (await fetchResourceFromURI(uri)) as Blob
    const contentType = file?.type || "image/jpeg"
    const signed = await axios.post(
        "/vapor/signed-storage-url",
        { bucket: "", content_type: contentType, expires: "", visibility: "" },
        { baseURL: API_URL }
    )
    const response = await fetch(signed.data.url, { method: "PUT", headers: signed.data.headers, body: file })
    if (!response.ok) throw new PhotoUploadError(response.status)
    return signed.data.key as string
}

export const defaultCheckpointApi: CheckpointApi = {
    create: async (body) => payload(await liveService.createConversation({}, body)),
    respond: async (conversationId, body) =>
        payload(await liveService.respondConversation({ id: conversationId }, body)),
    finish: (conversationId, body) => liveService.finishConversation({ conversation_id: conversationId }, body),
    feedback: (conversationId, value) =>
        liveService.giveAgentFeedback({ id: conversationId }, { feedback_type: "student", feedback_value: value }),
    awardPetal: (conversationId) => liveService.awardCheckpointPetal({ conversation_id: conversationId }),
    uploadPhoto,
}
